import React, { lazy, useEffect } from 'react';
import { HeaderMfe } from '|/app/header';
import SlateHeader from '|/app/header/slate-header';
import CancelOrderModalListener from '|/app/purchase/checkout/cancel-order-modal-listener';
import { setChatPageContext } from '|/app/purchase/checkout/constants/chat-page-context';
import { getChatSurface } from '|/app/purchase/checkout/constants/chat-surface-mappings';
import EditOrderModalListener from '|/app/purchase/checkout/edit-order-modal-listener';
import { Step } from '|/app/services';
import Loading from '|/components/base/loading';
import { FormSuspense, StepUrlsContextProvider, useSlate } from '|/context';
import { AppFrameworkContextProvider } from '|/helpers/app-framework';
import { useOpdShellMode } from '|/hooks/use-opd-shell-mode';
import registry from '~/library/store/src/registry';
import OpdMfeRoute from './opd-mfe-route';
import stepLayoutStyles from '../../shared/step-layout.module.css';

const OrderPlacedComponent = lazy(
  () =>
    import(
      /* webpackChunkName: "carvana-order-placed" */ '@carvana/purchase-order-placed'
    ) as unknown as Promise<{ default: React.ComponentType<any> }>
);

interface OrderPlacedRouteProps {
  steps: Step[];
}

export const OrderPlacedRoute: React.FC<OrderPlacedRouteProps> = ({ steps }) => {
  const { shellModeAllowed, isLoading } = useOpdShellMode();
  const { isSlateVehicle } = useSlate();

  useEffect(() => {
    const surface = getChatSurface('order-placed');
    if (surface) setChatPageContext(surface);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (shellModeAllowed) {
    return (
      <div className={stepLayoutStyles.opdLayout}>
        {isSlateVehicle ? <SlateHeader /> : <HeaderMfe orderSummary={null} />}
        <OpdMfeRoute />
        {/* OPD v2 fires core-events to open these shell-owned modals; mount the listeners
            here since this layout bypasses CheckoutPage / the nav-rail that host them. */}
        <CancelOrderModalListener />
        <EditOrderModalListener />
      </div>
    );
  }

  return (
    <>
      {isSlateVehicle ? <SlateHeader /> : <HeaderMfe orderSummary={null} />}
      <AppFrameworkContextProvider>
        <FormSuspense>
          <StepUrlsContextProvider steps={steps}>
            <div className={stepLayoutStyles.experimentOrderPlacedLayoutContainer}>
              <OrderPlacedComponent registry={registry} />
            </div>
          </StepUrlsContextProvider>
        </FormSuspense>
      </AppFrameworkContextProvider>
    </>
  );
};
